import React, { memo } from "react";
import {
  BlockOutlined,
  DeleteOutlined,
  DownOutlined,
  FolderOutlined,
  PushpinOutlined,
  SoundOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Badge,
  Card,
  Dropdown,
  Flex,
  MenuProps,
  Space,
  Typography,
} from "antd";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { useThemeContext } from "../../../context/ThemeContext";
import { BlockIcon, GroupIcon, MegaphoneIcon, MuteIcon } from "../../icons";
import "./index.scss";
import { storageService } from "../../../services/storageService";
import { GroupMember } from "../../../redux/interface";

const { Text, Title } = Typography;

interface ChatCardProps {
  item: any;
  handleClick: (id: string) => void;
  handleMute: () => void;
  handleBlock: () => void;
  handleArchive: () => void;
  handleDelete: () => void;
  handlePin: () => void;
}

const ChatCard: React.FC<ChatCardProps> = ({
  item,
  handleClick,
  handleMute,
  handleBlock,
  handleArchive,
  handleDelete,
  handlePin,
}) => {
  const { currentChat } = useSelector((state: RootState) => state.chat);
  const { theme } = useThemeContext();
  const userId = storageService.getUserId();
  const hasUnread =
    item?.unreadCount > 0 && String(item?.ofUser) !== String(userId);

  const isMember =
    item?.isGroup && item?.groupMembers && item?.groupMembers?.length > 0
      ? (item.groupMembers as GroupMember[]).some(
          (member) => String(member._id) === String(userId),
        )
      : true;

  const formatMessage = (msg: any) => {
    // If we have an actual message (from msg or item.message), show it.
    // We only show "You left" if the user is not a member and we don't have a fresh message.
    if (item?.isGroup && item?.groupMembers && item?.groupMembers?.length > 0) {
      if (!isMember && !msg) {
        return "You left";
      }
    }

    // use this for the time being, media type will be updated from backend
    return msg?.replace(/[{}]/g, "");
  };

  const menuItems: MenuProps["items"] = [
    !item.isGroup && {
      key: "mute",
      icon: <SoundOutlined />,
      label: item.isMuted ? "Unmute" : "Mute",
      onClick: handleMute,
    },
    !item.isGroup && { type: "divider" },
    !item.isGroup && {
      key: "block",
      icon: <BlockOutlined style={{ color: "red" }} />,
      label: item.isBlocked ? "Unblock" : "Block",
      onClick: handleBlock,
    },
    !item.isGroup && { type: "divider" },
    {
      key: "archive",
      icon: <FolderOutlined style={{ color: "#377DEF" }} />,
      label: item.isArchived ? "Un-Archive" : "Archive",
      onClick: handleArchive,
    },
    { type: "divider" },
    {
      key: "delete",
      icon: <DeleteOutlined style={{ color: "red" }} />,
      label: "Delete",
      danger: true,
      onClick: handleDelete,
    },
    { type: "divider" },
    {
      key: "pin",
      icon: <PushpinOutlined style={{ color: "#377DEF" }} />,
      label: item.isPin ? "Unpin" : "Pin",
      onClick: handlePin,
    },
  ].filter(Boolean) as MenuProps["items"];

  return (
    <Card
      onClick={() => {
        handleClick(item.id);
      }}
      style={{
        backgroundColor:
          item.id === currentChat?.id
            ? theme === "light"
              ? "#F5F5F5"
              : "#292929"
            : "",
      }}
      bordered={false}
      className={`chat-card${hasUnread ? " has-unread" : ""}`}
    >
      <Card.Meta
        avatar={
          <Avatar
            src={item.image}
            className={`avatar ${
              item.iseFone ? "efone-avatar" : "non-efone-avatar"
            }`}
          >
            {item.isBroadCast ? (
              <Flex className="broadcast-icon">
                <MegaphoneIcon />
              </Flex>
            ) : item.isGroup ? (
              <Flex className="group-icon">
                <GroupIcon />
              </Flex>
            ) : (
              <Text className="user-initial">
                {item?.savedName?.[0]?.toUpperCase() ||
                  item?.name?.[0]?.toUpperCase() ||
                  "+"}
              </Text>
            )}
          </Avatar>
        }
        title={
          <Flex justify="space-between" align="center">
            <Flex align="center" gap={4}>
              <Title
                title={item?.isGroup ? item?.name : item?.savedName}
                className="card-title"
              >
                {item?.isGroup || item?.isBroadCast
                  ? item?.name
                  : item?.savedName}
              </Title>
              <Badge
                count={item?.isefone ? "Free" : ""}
                className="free-badge"
              />
            </Flex>
            <Space>
              <Text className="date">{item?.date}</Text>
              <Dropdown
                overlayClassName="custom-dropdown"
                menu={{ items: menuItems }}
                trigger={["click"]}
              >
                <DownOutlined
                  className="down-icon"
                  onClick={(e) => e.stopPropagation()}
                />
              </Dropdown>
            </Space>
          </Flex>
        }
        description={
          <div className="description-container">
            <div>
              <div className="message-preview">
                {formatMessage(item?.message)}
              </div>

              <span className="badges-container">
                {item?.unreadCount > 0 &&
                  isMember &&
                  String(item?.ofUser) !== String(userId) && (
                    <div className="unread-count" key={item.unreadCount}>
                      {item?.unreadCount > 99 ? "99+" : item?.unreadCount}
                    </div>
                  )}
                {!item?.isGroup && item?.isMuted && (
                  <div style={{ color: "gray", display: "flex" }}>
                    <MuteIcon />
                  </div>
                )}
                {item?.isBlocked && (
                  <div className="block-badge">
                    <BlockIcon />
                  </div>
                )}
                {item?.isPin && <PushpinOutlined style={{ color: "gray" }} />}
              </span>
            </div>
            <Flex justify="end">
              <Text className="time">{item.time}</Text>
            </Flex>
          </div>
        }
      />
    </Card>
  );
};

export default memo(ChatCard);
